import React, { useState, useEffect } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import ContextualBreadcrumb from '../../components/ui/ContextualBreadcrumb';
import FloatingActionButton from '../../components/ui/FloatingActionButton';
import ProjectTabs from './components/ProjectTabs';
import ProjectCard from './components/ProjectCard';
import CompletedProjectCard from './components/CompletedProjectCard';
import EmptyState from './components/EmptyState';
import AIDraftCard from './components/AIDraftCard';
import { projectService } from '../../services/projectService';
import { supabase } from '../../lib/supabase';
import { realtimeService } from '../../services/realtimeService';
import { Briefcase, AlertCircle, Search } from 'lucide-react';

const normalizeText = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
const slugText = (value) => normalizeText(value).replace(/\s+/g, '-');

const derivePhaseStatus = (phase) => {
  const stored = normalizeText(phase?.status || phase?.phaseStatus);
  if (stored === 'completed' || stored === 'ongoing' || stored === 'pending') return stored;

  const progress = Number(phase?.progress ?? phase?.progressPercentage ?? 0);
  if (progress >= 100) return 'completed';
  if (progress > 0) return 'ongoing';
  return 'pending';
};

const statusLabelClass = (status) => {
  if (status === 'completed') return 'bg-green-100 text-green-700 border-green-200';
  if (status === 'ongoing') return 'bg-blue-100 text-blue-700 border-blue-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
};

const getPhaseTasks = (phase) => phase?.phase_tasks || phase?.phaseTasks || phase?.tasks || [];

const getProjectPhases = (project) => project?.phases || [];

const phaseMatchesTrade = (phase, selectedTrade) => {
  if (!phase || selectedTrade === 'all') return true;

  const selected = slugText(selectedTrade);
  const candidates = [
    phase?.phase_name,
    phase?.source_task_name,
    phase?.required_trade,
    phase?.trade,
    phase?.status,
  ]
    ?.map(slugText)
    ?.filter(Boolean);

  const phaseToTradeMap = {
    hacking: 'hacker',
    plumbing: 'plumber',
    electrical: 'electrical',
    electric: 'electrical',
    electrician: 'electrical',
    'false-ceiling': 'false-ceiling',
    tiling: 'tiling',
    carpentry: 'carpenter',
    carpenter: 'carpenter',
    painting: 'painter',
    paint: 'painter',
    aircon: 'aircon',
    cleaning: 'cleaner',
    clearing: 'cleaner',
  };

  return candidates?.some((candidate) => {
    if (candidate?.includes('3-musketeers')) {
      return ['plumber', 'electrical', 'aircon']?.includes(selected);
    }

    const directMatch = candidate === selected || candidate?.includes(selected) || selected?.includes(candidate);
    if (directMatch) return true;

    const mapped = phaseToTradeMap?.[candidate] || candidate;
    return mapped === selected;
  });
};

const projectMatchesSearch = (project, searchTerm) => {
  const q = normalizeText(searchTerm);
  if (!q) return true;

  const projectFields = [
    project?.name,
    project?.status,
    project?.lifecycle_status,
    project?.currentPhase,
    project?.source,
  ];

  const projectMatch = projectFields?.some((field) => normalizeText(field)?.includes(q));
  if (projectMatch) return true;

  return getProjectPhases(project)?.some((phase) => {
    const phaseStatus = derivePhaseStatus(phase);
    const phaseFields = [
      phase?.phase_name,
      phase?.source_task_name,
      phase?.required_trade,
      phase?.storey,
      phase?.owner_type,
      phase?.status,
      phaseStatus,
    ];

    const phaseMatch = phaseFields?.some((field) => normalizeText(field)?.includes(q));
    if (phaseMatch) return true;

    return getPhaseTasks(phase)?.some((task) =>
      normalizeText(task?.task_text || task?.name || task?.description)?.includes(q)
    );
  });
};

const projectMatchesStatus = (project, selectedStatus) => {
  if (!selectedStatus || selectedStatus === 'all') return true;

  const selected = normalizeText(selectedStatus);
  const projectStatus = normalizeText(project?.status);
  const lifecycleStatus = normalizeText(project?.lifecycle_status);

  if (projectStatus === selected || lifecycleStatus === selected) return true;

  return getProjectPhases(project)?.some((phase) => derivePhaseStatus(phase) === selected);
};

const projectMatchesTrade = (project, selectedTrade) => {
  if (!selectedTrade || selectedTrade === 'all') return true;

  const currentPhase = slugText(project?.currentPhase);
  if (currentPhase && phaseMatchesTrade({ phase_name: currentPhase }, selectedTrade)) return true;

  return getProjectPhases(project)?.some((phase) => phaseMatchesTrade(phase, selectedTrade));
};

const projectMatchesWorker = (project, workerId) => {
  if (!workerId || workerId === 'all') return true;

  return getProjectPhases(project)?.some((phase) => {
    if (phase?.assigned_worker_id === workerId) return true;
    return getPhaseTasks(phase)?.some((task) => task?.assigned_worker_id === workerId);
  });
};

const getWorkerAssignmentsForProject = (project, workerId) => {
  if (!workerId || workerId === 'all') return [];

  return getProjectPhases(project)
    ?.map((phase) => {
      const allTasks = getPhaseTasks(phase);
      const assignedTasks = allTasks?.filter((task) => task?.assigned_worker_id === workerId) || [];
      const wholePhaseAssigned = phase?.assigned_worker_id === workerId;

      if (!wholePhaseAssigned && assignedTasks?.length === 0) return null;

      return {
        ...phase,
        assignedTasks,
        wholePhaseAssigned,
        derivedStatus: derivePhaseStatus(phase),
      };
    })
    ?.filter(Boolean);
};

const CompactBossFilterBar = ({ filters, workers, onFilterChange }) => {
  const updateFilter = (key, value) => {
    onFilterChange?.({
      ...filters,
      [key]: value,
    });
  };

  const inputClass =
    'h-9 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  return (
    <div className="bg-card border border-border rounded-xl p-3 mt-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={filters?.search || ''}
            onChange={(e) => updateFilter('search', e?.target?.value || '')}
            placeholder="Search project, phase, status..."
            className={`${inputClass} w-full pl-9`}
          />
        </div>

        <div className="grid grid-cols-3 gap-2 lg:flex lg:items-center lg:flex-shrink-0">
          <select
            value={filters?.status || 'all'}
            onChange={(e) => updateFilter('status', e?.target?.value || 'all')}
            className={`${inputClass} w-full lg:w-32`}
          >
            <option value="all">All status</option>
            <option value="pending">Pending</option>
            <option value="ongoing">Ongoing</option>
            <option value="completed">Completed</option>
            <option value="on-track">On-track</option>
            <option value="delayed">Delayed</option>
          </select>

          <select
            value={filters?.trade || 'all'}
            onChange={(e) => updateFilter('trade', e?.target?.value || 'all')}
            className={`${inputClass} w-full lg:w-32`}
          >
            <option value="all">All trade</option>
            <option value="hacker">Hacker</option>
            <option value="plumber">Plumber</option>
            <option value="electrical">Electrical</option>
            <option value="aircon">Aircon</option>
            <option value="false-ceiling">False ceiling</option>
            <option value="tiling">Tiling</option>
            <option value="carpenter">Carpenter</option>
            <option value="painter">Painter</option>
            <option value="cleaner">Cleaner</option>
          </select>

          <select
            value={filters?.worker || 'all'}
            onChange={(e) => updateFilter('worker', e?.target?.value || 'all')}
            className={`${inputClass} w-full lg:w-36`}
          >
            <option value="all">All workers</option>
            {workers?.map((worker) => (
              <option key={worker?.id} value={worker?.id}>
                {worker?.full_name || worker?.email || 'Unnamed'}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

const WorkerWorkloadCard = ({ project, workerId }) => {
  const assignments = getWorkerAssignmentsForProject(project, workerId);

  if (!assignments?.length) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-foreground truncate">{project?.name}</h3>
            {project?.source === 'ai_import' && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">
                AI Imported
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {assignments?.length} assigned phase{assignments?.length !== 1 ? 's' : ''}
          </p>
        </div>

        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium bg-slate-100 text-slate-600 border-slate-200">
          <Briefcase size={12} />
          {project?.lifecycle_status || project?.status || 'project'}
        </span>
      </div>

      <div className="space-y-2">
        {assignments?.map((phase) => {
          const phaseStatus = phase?.derivedStatus || derivePhaseStatus(phase);

          return (
            <div key={phase?.id} className="rounded-lg border border-border bg-muted/20 px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground truncate">
                  {phase?.phase_name || phase?.source_task_name || 'Unnamed phase'}
                </p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-medium flex-shrink-0 ${statusLabelClass(phaseStatus)}`}>
                  {phaseStatus}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BossHomePage = () => {
  const [activeTab, setActiveTab] = useState('ongoing');
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    trade: 'all',
    worker: 'all',
  });

  const [ongoingProjects, setOngoingProjects] = useState([]);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [filteredOngoingProjects, setFilteredOngoingProjects] = useState([]);
  const [filteredCompletedProjects, setFilteredCompletedProjects] = useState([]);
  const [draftProjects, setDraftProjects] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [draftProcessing, setDraftProcessing] = useState(false);
  const [draftActionError, setDraftActionError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [filters, ongoingProjects, completedProjects]);

  const attachPhaseTasksToProjects = async (projects = []) => {
    const phaseIds = projects
      ?.flatMap((project) => getProjectPhases(project))
      ?.map((phase) => phase?.id)
      ?.filter(Boolean);

    if (!phaseIds?.length) return projects || [];

    const { data: tasks, error: taskError } = await supabase
      ?.from('phase_tasks')
      ?.select('*')
      ?.in('phase_id', phaseIds)
      ?.order('task_order', { ascending: true });

    if (taskError) {
      console.error('Error loading phase tasks for boss homepage:', taskError);
      return projects || [];
    }

    const tasksByPhaseId = {};
    (tasks || [])?.forEach((task) => {
      if (!tasksByPhaseId?.[task?.phase_id]) tasksByPhaseId[task?.phase_id] = [];
      tasksByPhaseId?.[task?.phase_id]?.push(task);
    });

    return (projects || [])?.map((project) => ({
      ...project,
      phases: getProjectPhases(project)?.map((phase) => ({
        ...phase,
        phase_tasks: tasksByPhaseId?.[phase?.id] || getPhaseTasks(phase) || [],
        phaseStatus: derivePhaseStatus(phase),
      })),
    }));
  };

  const loadWorkers = async () => {
    const { data, error: workerError } = await supabase
      ?.from('users')
      ?.select('id, full_name, trade, email')
      ?.order('full_name', { ascending: true });

    if (workerError) {
      console.error('Error loading workers:', workerError);
      return [];
    }

    return data || [];
  };

  // Handle real-time phase updates
  const handlePhaseUpdate = (updatedPhase) => {
    console.log('Real-time phase update received (boss view):', updatedPhase);

    const applyPhaseUpdate = (prevProjects) =>
      prevProjects?.map((project) => {
        const phaseIndex = getProjectPhases(project)?.findIndex((p) => p?.id === updatedPhase?.id);
        if (phaseIndex === -1) return project;

        const derivedStatus = derivePhaseStatus(updatedPhase);
        const updatedPhases = [...getProjectPhases(project)];
        updatedPhases[phaseIndex] = {
          ...updatedPhases?.[phaseIndex],
          ...updatedPhase,
          progress: updatedPhase?.progress,
          progressPercentage: updatedPhase?.progress,
          status: derivedStatus,
          phaseStatus: derivedStatus,
          phase_tasks: updatedPhases?.[phaseIndex]?.phase_tasks || [],
        };

        const currentPhase = updatedPhases?.find((p) => p?.progress > 0 && p?.progress < 100);

        return {
          ...project,
          phases: updatedPhases,
          phaseProgress: currentPhase?.progress || project?.phaseProgress,
        };
      });

    setOngoingProjects(applyPhaseUpdate);
    setCompletedProjects(applyPhaseUpdate);
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError('');

      const { data: { session } } = await supabase?.auth?.getSession();

      if (!session) {
        setError('You must be logged in to view projects. Please log in to continue.');
        setLoading(false);
        return;
      }

      const [projects, drafts, loadedWorkers] = await Promise.all([
        projectService?.getAllProjects(),
        projectService?.getDraftProjects()?.catch(() => []),
        loadWorkers()?.catch(() => []),
      ]);

      const projectsWithTasks = await attachPhaseTasksToProjects(projects || []);

      const ongoing = projectsWithTasks?.filter(
        (p) => p?.status !== 'completed' && p?.status !== 'draft' && p?.lifecycle_status !== 'completed'
      );
      const completed = projectsWithTasks?.filter(
        (p) => p?.status === 'completed' || p?.lifecycle_status === 'completed'
      );

      setWorkers(loadedWorkers || []);
      setOngoingProjects(ongoing);
      setCompletedProjects(completed);
      setFilteredOngoingProjects(ongoing);
      setFilteredCompletedProjects(completed);
      setDraftProjects(drafts || []);

      realtimeService?.subscribeToAllPhaseUpdates(handlePhaseUpdate);
    } catch (err) {
      console.error('Error loading projects:', err);
      setError(err?.message || 'Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      realtimeService?.unsubscribeAll();
    };
  }, []);

  const filterProjectList = (projects) => {
    return [...(projects || [])]?.filter((project) => {
      return (
        projectMatchesSearch(project, filters?.search) &&
        projectMatchesStatus(project, filters?.status) &&
        projectMatchesTrade(project, filters?.trade) &&
        projectMatchesWorker(project, filters?.worker)
      );
    });
  };

  const filterProjects = () => {
    setFilteredOngoingProjects(filterProjectList(ongoingProjects));
    setFilteredCompletedProjects(filterProjectList(completedProjects));
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const handlePaymentUpdate = async (projectId, isPaid) => {
    try {
      await projectService?.updatePaymentStatus(projectId, isPaid);
      await loadProjects();
    } catch (err) {
      console.error('Error updating payment status:', err);
      setError(err?.message);
    }
  };

  const handleDelete = async (projectId) => {
    try {
      const allProjects = [...ongoingProjects, ...completedProjects];
      const project = allProjects?.find((p) => p?.id === projectId);

      if (project?.source === 'ai_import' && project?.ai_import_id) {
        const { data: { session } } = await supabase?.auth?.getSession();
        const response = await fetch(
          `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/dynamic-function`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.access_token}`,
              apikey: import.meta.env?.VITE_SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({ ai_import_id: project?.ai_import_id }),
          }
        );

        const result = await response?.json();

        if (!response?.ok) {
          throw new Error(result?.error || 'Failed to delete AI-imported project');
        }

        await loadProjects();
      } else {
        await projectService?.deleteProject(projectId);
        await loadProjects();
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      setError(err?.message || 'Failed to delete project');
      throw err;
    }
  };

  const handleApproveDraft = async (projectId, projectName) => {
    if (!window.confirm(`Approve "${projectName}"? It will become an active project.`)) return;
    setDraftProcessing(true);
    setDraftActionError('');
    try {
      await projectService?.approveDraftProject(projectId);
      await loadProjects();
    } catch (err) {
      console.error('Error approving draft:', err);
      setDraftActionError(err?.message || 'Failed to approve project');
    } finally {
      setDraftProcessing(false);
    }
  };

  const handleRejectDraft = async (projectId, projectName) => {
    if (!window.confirm(`Reject and delete "${projectName}"? This cannot be undone.`)) return;
    setDraftProcessing(true);
    setDraftActionError('');
    try {
      await projectService?.rejectDraftProject(projectId);
      await loadProjects();
    } catch (err) {
      console.error('Error rejecting draft:', err);
      setDraftActionError(err?.message || 'Failed to reject project');
    } finally {
      setDraftProcessing(false);
    }
  };

  const selectedWorker = workers?.find((worker) => worker?.id === filters?.worker);
  const workerFilterActive = filters?.worker && filters?.worker !== 'all';

  const hasActiveFilters =
    filters?.search || filters?.status !== 'all' || filters?.trade !== 'all' || workerFilterActive;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <ContextualBreadcrumb />
        <div className="main-content-mobile-nav flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <ContextualBreadcrumb />
        <div className="main-content-mobile-nav">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <ContextualBreadcrumb />

      <div className="main-content-mobile-nav">
        <ProjectTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ongoingCount={filteredOngoingProjects?.length}
          completedCount={filteredCompletedProjects?.length}
          draftCount={draftProjects?.length}
        />

        <CompactBossFilterBar
          filters={filters}
          workers={workers}
          onFilterChange={handleFilterChange}
        />

        {workerFilterActive && (
          <div className="mt-4 p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm text-blue-800 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              Showing workload for <span className="font-semibold">{selectedWorker?.full_name || 'selected worker'}</span>. Pending phases are included and labelled clearly.
            </span>
          </div>
        )}

        <div className="py-6">
          {activeTab === 'ongoing' && (
            <>
              {filteredOngoingProjects?.length > 0 ? (
                workerFilterActive ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {filteredOngoingProjects?.map((project) => (
                      <WorkerWorkloadCard
                        key={project?.id}
                        project={project}
                        workerId={filters?.worker}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {filteredOngoingProjects?.map((project) => (
                      <ProjectCard
                        key={project?.id}
                        project={project}
                        userRole="boss"
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                )
              ) : (
                <EmptyState
                  type={hasActiveFilters ? 'filtered' : 'ongoing'}
                  userRole="boss"
                />
              )}
            </>
          )}

          {activeTab === 'completed' && (
            <>
              {filteredCompletedProjects?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredCompletedProjects?.map((project) => (
                    <CompletedProjectCard
                      key={project?.id}
                      project={project}
                      userRole="boss"
                      onPaymentUpdate={handlePaymentUpdate}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  type={hasActiveFilters ? 'filtered' : 'completed'}
                  userRole="boss"
                />
              )}
            </>
          )}

          {activeTab === 'drafts' && (
            <>
              {draftActionError && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive">{draftActionError}</p>
                </div>
              )}
              {draftProjects?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {draftProjects?.map((project) => (
                    <AIDraftCard
                      key={project?.id}
                      project={project}
                      onApprove={handleApproveDraft}
                      onReject={handleRejectDraft}
                      isProcessing={draftProcessing}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  type="drafts"
                  userRole="boss"
                />
              )}
            </>
          )}
        </div>
      </div>

      <FloatingActionButton userRole="boss" />
    </div>
  );
};

export default BossHomePage;
